import { UNIQUE_CONFIG_KEY } from '../../../shared/constants';

export interface ILayoutShiftSource {
  node?: Node | null;
  previousRect?: DOMRectReadOnly;
  currentRect?: DOMRectReadOnly;
}

export interface ILayoutShift extends PerformanceEntry {
  value: number;
  hadRecentInput: boolean;
  sources?: ILayoutShiftSource[];
}

export interface IOwnershipSplit {
  /** Двигались потомки корня — перестроилась наша разметка. */
  ours: number;
  /**
   * Источником числится сам корень приложения. Он себя не двигает: значит его
   * толкнуло то, что выше — шапка, баннер, любой внешний блок. Мы тут пострадавший.
   */
  rootMoved: number;
  /** Все источники вне корня — шапка, футер, сторонние виджеты. */
  foreign: number;
  /** Сдвиг без источников: браузер их не отдал. */
  unknown: number;
  /** scrollY в момент самого крупного сдвига — не итоговый, а именно тогда. */
  scrollYAtWorst: number;
  worstValue: number;
  /** Источники крупнейшего сдвига: по ним видно, кто менял высоту, а кого просто сдвинуло. */
  worstSources: string[];
  /** Сколько занято над корнем приложения: по сути высота шапки. */
  headerHeightStart: number | null;
  headerHeightEnd: number | null;
}

export type TOwner = 'ours' | 'rootMoved' | 'foreign';

export function getHeaderHeight(): number | null {
  const root = document.getElementById(UNIQUE_CONFIG_KEY);

  return root ? Math.round(root.getBoundingClientRect().top + window.scrollY) : null;
}

/** Читаемый селектор источника. Приоритет — по стабильности, хешированные классы последними. */
export function getSelector(node: Node | null | undefined): string {
  if (!node || node.nodeType !== Node.ELEMENT_NODE) {
    return '(нет узла)';
  }

  const element = node as Element;
  const tag = element.tagName.toLowerCase();

  if (element.id) {
    return `${tag}#${element.id}`;
  }

  const dataName = element.getAttribute('data-name') || element.getAttribute('data-testid');

  if (dataName) {
    return `${tag}[${dataName}]`;
  }

  const classes = (element.getAttribute('class') || '').split(/\s+/).filter(Boolean).slice(0, 2);

  return classes.length ? `${tag}.${classes.join('.')}` : tag;
}

const signed = (value: number) => (value > 0 ? `+${value}` : String(value));

/**
 * Источник в одну строку: `селектор dy=… dh=… dx=… dw=…`.
 *
 * Все четыре величины обязательны. Первая версия писала только `dy` и `dh` —
 * и оказалась слепой там, где элементы шапки менялись по горизонтали:
 * в логах у них стояли нули, хотя сдвиг именно от них и шёл.
 *
 * Логгер не принимает вложенные объекты в мета, да и в Discover строки читаются удобнее.
 */
export function serializeSources(sources: ILayoutShiftSource[]): string[] {
  return sources.slice(0, 5).map(source => {
    const selector = getSelector(source.node);
    const before = source.previousRect;
    const after = source.currentRect;

    if (!before || !after) {
      return `${selector} dy=? dh=? dx=? dw=?`;
    }

    const dy = signed(Math.round(after.y - before.y));
    const dh = signed(Math.round(after.height - before.height));
    const dx = signed(Math.round(after.x - before.x));
    const dw = signed(Math.round(after.width - before.width));

    return `${selector} dy=${dy} dh=${dh} dx=${dx} dw=${dw}`;
  });
}

interface INetworkInformation {
  effectiveType?: string;
  rtt?: number;
  downlink?: number;
  saveData?: boolean;
}

export interface ISessionInfo {
  /** 4g / 3g / 2g / slow-2g — оценка канала самим браузером. */
  effectiveType: string | null;
  /** Оценка круговой задержки, мс. */
  rtt: number | null;
  /** Гигабайты памяти и число ядер: отличает слабое железо от быстрого. */
  deviceMemory: number | null;
  hardwareConcurrency: number | null;
}

/** Характеристики сессии. Ничего персонального: класс канала и класс железа. */
export function getSessionInfo(): ISessionInfo {
  const nav = navigator as Navigator & { connection?: INetworkInformation; deviceMemory?: number };
  const conn = nav.connection;

  return {
    effectiveType: conn?.effectiveType ?? null,
    rtt: typeof conn?.rtt === 'number' ? conn.rtt : null,
    deviceMemory: typeof nav.deviceMemory === 'number' ? nav.deviceMemory : null,
    hardwareConcurrency: typeof navigator.hardwareConcurrency === 'number' ? navigator.hardwareConcurrency : null,
  };
}

/** Высота нашего корня: выросла наша часть после гидратации или сжалась. */
export function getRootHeight(): number | null {
  const root = document.getElementById(UNIQUE_CONFIG_KEY);

  return root ? Math.round(root.getBoundingClientRect().height) : null;
}

/**
 * CLS показывает пострадавший элемент, а не виновника. Поэтому различаем три случая:
 * сам корень уехал (виноват кто-то снаружи), уехали его потомки (виноваты мы),
 * уехало что-то вне корня (виноват кто-то снаружи и нас не задело).
 */
export function classifySource(node: Node | null | undefined): TOwner {
  if (!node || node.nodeType !== Node.ELEMENT_NODE) {
    return 'foreign';
  }

  const element = node as Element;

  if (element.id === UNIQUE_CONFIG_KEY) {
    return 'rootMoved';
  }

  return element.closest(`#${UNIQUE_CONFIG_KEY}`) ? 'ours' : 'foreign';
}

/**
 * Готовый ответ на «кто виноват», одной строкой.
 * Логгер раскладывает мету в коллекцию пар key/value, поэтому в ELK удобнее
 * фильтровать по заранее посчитанной категории, чем собирать её запросом.
 */
export function getCulprit(split: IOwnershipSplit): string {
  const { headerHeightStart: start, headerHeightEnd: end } = split;

  if (start !== null && end !== null && start !== end) {
    return 'header';
  }

  const max = Math.max(split.ours, split.rootMoved, split.foreign, split.unknown);

  if (max === 0) {
    return 'none';
  }

  if (max === split.rootMoved) {
    return 'outside';
  }

  if (max === split.ours) {
    return 'ours';
  }

  return max === split.foreign ? 'foreign' : 'unknown';
}

/** Как пользователь попал на страницу — от этого зависит, где стоял экран к моменту гидратации. */
export function getEntryType(navType: string | null): string {
  if (window.location.hash) {
    return 'anchor';
  }

  return navType === 'back_forward' || navType === 'reload' ? 'restored' : 'plain';
}
